import { useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { SectionLabel } from '../components/ui/SectionLabel';
import { SurfaceCard } from '../components/ui/SurfaceCard';
import { getRuntimeConfig } from '../config/runtimeConfig';
import { useSession } from '../session/SessionProvider';
import { useWeatherFeed } from '../weather/WeatherProvider';

/**
 * Cât timp o cădere trece drept pâlpâire.
 *
 * Socket-ul se reconectează singur cu pauze crescătoare, iar o repornire de
 * backend ține câteva secunde. Sub pragul ăsta lăsăm valorile pe ecran: a goli
 * dashboardul pentru o întrerupere de-o secundă e mai supărător decât util.
 */
const GRACE_MS = 12_000;

/**
 * Poarta aplicației: dashboardul se vede doar cât timp chiar avem legătură cu stația.
 *
 * Verificarea nu e una separată — biletul de acces și socket-ul *sunt* proba.
 * Un ping în plus ar fi doar încă un lucru care poate minți.
 *
 * La pornire, un bilet refuzat oprește totul pe loc. După încărcare, o cădere
 * trebuie să țină mai mult de `GRACE_MS` ca să înlocuiască ecranul — între timp
 * rămân ultimele valori, cu starea conexiunii scrisă în antet.
 */
export function StartupGate({ children }: { children: ReactNode }) {
  const { status, errorStatus, renew } = useSession();
  const { status: connection } = useWeatherFeed();

  const healthy = status === 'demo' || (status === 'ready' && connection === 'connected');

  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (healthy) setStarted(true);
  }, [healthy]);

  // Cronometrul pornește la fiecare întrerupere și se anulează dacă legătura
  // revine, deci numai o cădere care ține închide dashboardul.
  const [lost, setLost] = useState(false);
  useEffect(() => {
    if (healthy) {
      setLost(false);
      return;
    }
    const timer = window.setTimeout(() => setLost(true), GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [healthy]);

  if (healthy || (started && !lost)) return <>{children}</>;

  // Biletul refuzat e un verdict, nu o așteptare: nu mai are rost răbdarea.
  if (status === 'error' || lost) {
    return (
      <MaintenanceScreen
        // Un 401/403 înseamnă că stația e sus și te refuză. Alt necaz, alt mesaj.
        refused={errorStatus === 401 || errorStatus === 403}
        onRetry={renew}
      />
    );
  }

  return <ConnectingScreen />;
}

/** Fundalul comun al celor două ecrane, ca să semene cu dashboardul. */
function Screen({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.weather.bg,
        color: theme.palette.weather.ink,
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 'clamp(20px, 4vw, 48px)',
        transition: 'background-color .5s ease, color .5s ease',
      })}
    >
      {children}
    </Box>
  );
}

/**
 * Așteptarea biletului.
 *
 * Apare cu o întârziere mică: pe o rețea bună răspunsul vine în câteva zeci de
 * milisecunde, iar un mesaj care clipește o dată e mai enervant decât tăcerea.
 */
function ConnectingScreen() {
  return (
    <Screen>
      <Box
        sx={{
          display: 'grid',
          justifyItems: 'center',
          gap: '18px',
          opacity: 0,
          animation: 'startup-appear .4s ease .35s forwards',
          '@keyframes startup-appear': { to: { opacity: 1 } },
        }}
      >
        <CircularProgress size={26} sx={(theme) => ({ color: theme.palette.weather.accent })} />
        <Typography sx={(theme) => ({ fontSize: 14, color: theme.palette.weather.soft })}>
          Se conectează la stație…
        </Typography>
      </Box>
    </Screen>
  );
}

interface MaintenanceScreenProps {
  refused: boolean;
  onRetry: () => void;
}

/**
 * Fără amănunte tehnice: cine deschide pagina n-are ce face cu ele, iar cine
 * poate repara se uită în loguri, nu în interfață.
 */
function MaintenanceScreen({ refused, onRetry }: MaintenanceScreenProps) {
  return (
    <Screen>
      <SurfaceCard
        sx={{
          maxWidth: 460,
          alignItems: 'center',
          textAlign: 'center',
          gap: '14px',
          padding: 'clamp(28px, 4vw, 44px)',
        }}
      >
        <SectionLabel>{getRuntimeConfig().stationName}</SectionLabel>

        <Typography
          component="h1"
          sx={{
            margin: 0,
            fontSize: 'clamp(21px, 2.8vw, 28px)',
            fontWeight: 600,
            letterSpacing: '-.02em',
            lineHeight: 1.2,
          }}
        >
          {refused ? 'Acces neautorizat' : 'Aplicația este în mentenanță'}
        </Typography>

        <Typography sx={(theme) => ({ fontSize: 14, color: theme.palette.weather.soft })}>
          {refused
            ? 'Stația răspunde, dar nu îți dă acces.'
            : 'Stația nu răspunde. Se reîncearcă automat.'}
        </Typography>

        <Button
          onClick={onRetry}
          startIcon={<RefreshRoundedIcon />}
          sx={(theme) => ({
            marginTop: '4px',
            height: 38,
            padding: '0 18px 0 14px',
            borderRadius: `${theme.radii.pill}px`,
            backgroundColor: theme.palette.weather.accentSoft,
            color: theme.palette.weather.accent,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { backgroundColor: theme.palette.weather.accentSoft, boxShadow: 'none' },
          })}
        >
          Reîncearcă acum
        </Button>
      </SurfaceCard>
    </Screen>
  );
}
